import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { 
  Car, ChevronLeft, ChevronRight, Plus, Clock, CheckCircle, 
  AlertTriangle, Package, PackageCheck, Edit2, Printer
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { addWeeks, subWeeks, startOfWeek, format, addDays, isSameDay, getWeek } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

// Vehicle card component - ULTRA COMPACT with description
function VehicleCard({ vehicle, navigate, getStepColor, getStepLabel, columnDate }) {
  const clientName = vehicle.client?.nom || "";
  
  // Priorité : la gommette apparait à partir de priorite_semaine (comparaison par date locale sans timezone)
  const checkDateStr = columnDate ? format(columnDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
  const isPriority = vehicle.priorite && vehicle.type_intervention !== "mecanique" && (
    !vehicle.priorite_semaine || vehicle.priorite_semaine.substring(0, 10) <= checkDateStr
  );
  
  return (
    <div
      onClick={() => navigate(`/vehicles/${vehicle.id}`)}
      className={`px-1.5 py-1 rounded border cursor-pointer transition-all hover:border-red-400 text-[10px] leading-tight relative ${
        isPriority ? 'ring-2 ring-red-500 ring-offset-1' :
        vehicle.is_blocked ? 'bg-red-50 border-red-300' : 'bg-white border-slate-200'
      }`}
    >
      {/* Gommette priorité */}
      {isPriority && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white" title="Prioritaire" />
      )}
      
      {/* Line 1: Client name + Hour */}
      <div className="flex items-center justify-between gap-1">
        <span className="font-semibold text-slate-800 truncate">{clientName}</span>
        {vehicle.heure_rdv && (
          <span className="text-blue-600 font-bold flex-shrink-0">{vehicle.heure_rdv}</span>
        )}
      </div>
      
      {/* Line 2: Marque + Modèle */}
      {(vehicle.marque || vehicle.modele) && (
        <p className="text-[9px] text-slate-600 truncate font-medium">
          {vehicle.marque} {vehicle.modele}
        </p>
      )}
      
      {/* Line 3: Description/Notes - truncated */}
      {vehicle.description && (
        <p className="text-[9px] text-slate-500 truncate italic">
          {vehicle.description}
        </p>
      )}
      
      {/* Line 4: Immat + Badge + Pieces */}
      <div className="flex items-center justify-between gap-1 mt-0.5">
        <span className="font-mono font-bold text-slate-600 truncate">{vehicle.immatriculation}</span>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <span className={`${getStepColor(vehicle.workflow_step)} px-1 py-0 rounded text-[8px] font-bold`}>
            {getStepLabel(vehicle.workflow_step)}
          </span>
          {vehicle.pieces_commandees ? (
            <PackageCheck className="h-3 w-3 text-emerald-500" />
          ) : (
            <Package className="h-3 w-3 text-red-500" />
          )}
        </div>
      </div>
    </div>
  );
}

// Mécanique Day Column with HOURS BAR (8h = 100%)
function MecaniqueDayColumn({ day, vehiclesList, loading, navigate, workflowSteps, chargeData }) {
  const isToday = isSameDay(day, new Date());
  const isClosed = chargeData?.meca_closed;
  
  if (isClosed) {
    return (
      <div className="flex flex-col rounded-lg border overflow-hidden border-slate-200 opacity-50">
        <div className="px-2 py-1 bg-red-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-bold uppercase text-slate-500">{format(day, "EEE", { locale: fr })}</span>
              <span className="text-xs font-black text-slate-800">{format(day, "d")}</span>
            </div>
          </div>
        </div>
        <div className="flex-1 p-1 bg-red-50/50 flex items-center justify-center" style={{ minHeight: '150px' }}>
          <span className="text-red-500 text-xs font-bold">{chargeData?.meca_closed_label || "Fermé"}</span>
        </div>
      </div>
    );
  }
  
  // Calculer les heures totales occupées (max 8h par jour pour l'affichage)
  const totalHours = vehiclesList.reduce((sum, v) => sum + Math.min(v.duree_estimee_heures || 2, 8), 0);
  const hoursLeft = Math.max(0, 8 - totalHours);
  const occupancyPercent = Math.min(100, (totalHours / 8) * 100);
  
  return (
    <div className={`flex flex-col rounded-lg border overflow-hidden ${
      isToday ? 'border-red-500 ring-2 ring-red-100' : 'border-slate-200'
    }`}>
      {/* Day header avec barre d'heures */}
      <div className={`px-2 py-1 ${isToday ? 'bg-red-50' : 'bg-emerald-50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className="text-[10px] font-bold uppercase text-slate-500">
              {format(day, "EEE", { locale: fr })}
            </span>
            <span className={`text-xs font-black ${isToday ? 'text-red-600' : 'text-slate-800'}`}>
              {format(day, "d")}
            </span>
          </div>
          <span className={`text-[10px] font-bold ${hoursLeft > 2 ? 'text-emerald-600' : hoursLeft > 0 ? 'text-amber-600' : 'text-red-600'}`}>
            {hoursLeft > 0 ? `${hoursLeft}h dispo` : 'Complet'}
          </span>
        </div>
        {/* Barre de progression */}
        <div className="mt-1 h-2 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all ${
              occupancyPercent >= 100 ? 'bg-red-500' : 
              occupancyPercent >= 75 ? 'bg-amber-500' : 'bg-emerald-500'
            }`}
            style={{ width: `${occupancyPercent}%` }}
          />
        </div>
      </div>
      
      {/* Véhicules empilés - tous visibles */}
      <div className="flex-1 p-1 bg-slate-50/50 space-y-1" style={{ minHeight: '150px' }}>
        {loading ? (
          <div className="flex justify-center py-2">
            <div className="spinner w-3 h-3" />
          </div>
        ) : vehiclesList.length === 0 ? (
          <div className="h-full border-2 border-dashed border-emerald-300 rounded-lg flex items-center justify-center" style={{ minHeight: '140px' }}>
            <span className="text-emerald-500 text-xs font-medium">8h libres</span>
          </div>
        ) : (
          <>
            {vehiclesList.map((vehicle) => {
              const hours = vehicle.duree_estimee_heures || 2;
              // Hauteur proportionnelle mais avec un min/max raisonnable
              const heightPx = Math.max(40, Math.min(80, (hours / 8) * 150));
              
              return (
                <div
                  key={vehicle.id}
                  onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                  className={`px-1.5 py-1 rounded border cursor-pointer transition-all hover:border-red-400 text-[10px] leading-tight overflow-hidden ${
                    vehicle.is_blocked ? 'bg-red-50 border-red-300' : 'bg-white border-emerald-200'
                  }`}
                  style={{ minHeight: `${heightPx}px` }}
                >
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-mono font-bold text-slate-800 truncate">
                      {vehicle.immatriculation}
                    </span>
                    <span className="text-emerald-600 font-bold flex-shrink-0">{hours}h</span>
                  </div>
                  <p className="text-[9px] text-slate-600 truncate">
                    {vehicle.marque} {vehicle.modele}
                  </p>
                  {vehicle.description && (
                    <p className="text-[9px] text-slate-500 truncate italic">{vehicle.description}</p>
                  )}
                  <div className="flex items-center gap-0.5 mt-0.5">
                    <span className="text-blue-600 text-[9px]">{vehicle.heure_rdv}</span>
                    {vehicle.workflow_step && vehicle.workflow_step !== "none" && vehicle.workflow_step !== "termine" && (() => {
                      const stepData = (workflowSteps || []).find(s => s.name === vehicle.workflow_step);
                      const label = stepData?.label?.substring(0, 10) || vehicle.workflow_step.substring(0, 10);
                      return (
                        <span className={`step-${stepData?.color || 'blue'} px-1 py-0 rounded text-[8px] font-bold`}>
                          {label}
                        </span>
                      );
                    })()}
                    {vehicle.pieces_commandees ? (
                      <PackageCheck className="h-3 w-3 text-emerald-500" />
                    ) : (
                      <Package className="h-3 w-3 text-red-500" />
                    )}
                  </div>
                </div>
              );
            })}
            {/* Zone disponible si reste des heures */}
            {hoursLeft > 0 && (
              <div className="border-2 border-dashed border-emerald-300 rounded-lg flex items-center justify-center bg-emerald-50/30 py-2">
                <span className="text-emerald-600 text-[10px] font-medium">{hoursLeft}h libres</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Day column component - NO SCROLL, all content visible
function DayColumn({ day, vehiclesList, colorClass, loading, navigate, getChargeForDay, getStepColor, getStepLabel, chargeData }) {
  const isToday = isSameDay(day, new Date());
  const isClosed = chargeData?.carro_closed;
  
  if (isClosed) {
    return (
      <div className="flex flex-col rounded-lg border overflow-hidden border-slate-200 opacity-50">
        <div className="px-1 py-0.5 text-center flex-shrink-0 bg-red-100">
          <div className="flex items-center justify-center gap-1">
            <span className="text-[10px] font-bold uppercase text-slate-500">{format(day, "EEE", { locale: fr })}</span>
            <span className="text-xs font-black text-slate-800">{format(day, "d", { locale: fr })}</span>
          </div>
        </div>
        <div className="flex-1 p-1 bg-red-50/50 flex items-center justify-center">
          <span className="text-red-500 text-xs font-bold">{chargeData?.carro_closed_label || "Fermé"}</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`flex flex-col rounded-lg border overflow-hidden ${
      isToday ? 'border-red-500 ring-2 ring-red-100' : 'border-slate-200'
    }`}>
      {/* Day header - minimal */}
      <div className={`px-1 py-0.5 text-center flex-shrink-0 ${isToday ? 'bg-red-50' : colorClass}`}>
        <div className="flex items-center justify-center gap-1">
          <span className="text-[10px] font-bold uppercase text-slate-500">
            {format(day, "EEE", { locale: fr })}
          </span>
          <span className={`text-xs font-black ${isToday ? 'text-red-600' : 'text-slate-800'}`}>
            {format(day, "d", { locale: fr })}
          </span>
          {vehiclesList.length > 0 && (
            <span className={`text-[9px] font-bold px-1 rounded-full ${
              vehiclesList.length > 3 ? 'bg-red-200 text-red-700' :
              'bg-amber-200 text-amber-700'
            }`}>
              {vehiclesList.length}
            </span>
          )}
        </div>
      </div>
      
      {/* Vehicles list - NO SCROLL, compact */}
      <div className="flex-1 p-1 space-y-1 bg-slate-50/50">
        {loading ? (
          <div className="flex justify-center py-2">
            <div className="spinner w-3 h-3" />
          </div>
        ) : vehiclesList.length === 0 ? (
          <div className="text-center text-slate-300 text-[9px] py-1">-</div>
        ) : (
          vehiclesList.map((vehicle) => (
            <VehicleCard 
              key={vehicle.id} 
              vehicle={vehicle} 
              navigate={navigate}
              getStepColor={getStepColor}
              getStepLabel={getStepLabel}
              columnDate={day}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [charge, setCharge] = useState({});
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [weeksToShow, setWeeksToShow] = useState(1);
  const [viewMode, setViewMode] = useState("split");
  const [loading, setLoading] = useState(true);
  const [weekAnnotations, setWeekAnnotations] = useState({});
  const [editingAnnotation, setEditingAnnotation] = useState(null);
  const [annotationText, setAnnotationText] = useState("");
  const navigate = useNavigate();

  // Generate days for current week(s) (Mon-Fri only)
  const getWeekDays = () => {
    const days = [];
    for (let w = 0; w < weeksToShow; w++) {
      for (let d = 0; d < 5; d++) {
        days.push(addDays(addWeeks(currentWeek, w), d));
      }
    }
    return days;
  };

  const weekDays = getWeekDays();

  const fetchData = async () => {
    setLoading(true);
    try {
      const startDate = format(currentWeek, "yyyy-MM-dd");
      const endDate = format(addDays(addWeeks(currentWeek, weeksToShow), -1), "yyyy-MM-dd");
      
      const [vehiclesRes, statsRes, chargeRes, stepsRes, annotRes] = await Promise.all([
        api.get(`/vehicles?start_date=${startDate}&end_date=${endDate}&include_ongoing=true`),
        api.get("/stats/dashboard"),
        api.get(`/planning/charge?start_date=${startDate}&end_date=${endDate}`),
        api.get("/workflow-steps"),
        api.get(`/week-annotations?year=${new Date(startDate).getFullYear()}`)
      ]);
      
      setVehicles(vehiclesRes.data);
      setStats(statsRes.data);
      setCharge(chargeRes.data);
      setWorkflowSteps(stepsRes.data);
      setWeekAnnotations(annotRes.data || {});
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  };

  const saveAnnotation = async (weekNum) => {
    const year = new Date(currentWeek).getFullYear();
    try {
      await api.put(`/week-annotations/${year}/${weekNum}`, { text: annotationText });
      setWeekAnnotations(prev => ({ ...prev, [`${year}-${weekNum}`]: annotationText }));
      setEditingAnnotation(null);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeek, weeksToShow]);

  // Calcul de la date de fin en excluant les week-ends
  // Si durée = 1 jour, le véhicule apparaît uniquement le jour du RDV
  // Si durée = 3 jours et RDV le lundi, il apparaît lun-mar-mer
  const addBusinessDays = (startDate, numDays) => {
    if (numDays <= 1) return new Date(startDate);
    
    let result = new Date(startDate);
    let daysAdded = 1; // Le jour de départ compte comme jour 1
    
    while (daysAdded < numDays) {
      result = addDays(result, 1);
      // Ignorer les week-ends (0 = Dimanche, 6 = Samedi)
      if (result.getDay() !== 0 && result.getDay() !== 6) {
        daysAdded++;
      }
    }
    return result;
  };

  const getVehiclesForDay = (date) => {
    return vehicles.filter(v => {
      // Date du RDV
      const rdvDate = new Date(v.date_rdv);
      rdvDate.setHours(0, 0, 0, 0);
      
      // Date courante (celle de la colonne)
      const currentDate = new Date(date);
      currentDate.setHours(0, 0, 0, 0);
      
      // Durée estimée
      const duree = v.duree_estimee_jours || 1;
      
      // Calcul de la date de fin (en jours ouvrés)
      const endDate = addBusinessDays(rdvDate, duree);
      endDate.setHours(23, 59, 59, 999);
      
      // Aujourd'hui
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Ne pas afficher sur les week-ends
      const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;
      if (isWeekend) return false;
      
      // Véhicule terminé : afficher uniquement sur ses jours prévus
      if (v.statut === "termine") {
        return currentDate >= rdvDate && currentDate <= endDate;
      }
      
      // Véhicule non terminé :
      // 1. Afficher sur ses jours prévus (de rdvDate à endDate)
      if (currentDate >= rdvDate && currentDate <= endDate) {
        return true;
      }
      
      // 2. Si en retard (date de fin passée), afficher sur aujourd'hui et après
      if (endDate < today && currentDate >= today) {
        return true;
      }
      
      return false;
    });
  };

  const getChargeForDay = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return charge[dateStr] || { taux_occupation: 0, disponible: true, nb_vehicules: 0 };
  };

  const getStepColor = (step) => {
    const stepInfo = workflowSteps.find(s => s.name === step);
    if (stepInfo) return `step-${stepInfo.color}`;
    const colors = {
      termine: "bg-emerald-100 text-emerald-700",
      none: "bg-slate-100 text-slate-600",
    };
    return colors[step] || colors.none;
  };

  const getStepLabel = (step) => {
    if (step === "none") return "Méca";
    if (step === "termine") return "OK";
    const stepInfo = workflowSteps.find(s => s.name === step);
    return stepInfo?.label?.substring(0, 8) || step.substring(0, 8);
  };

  // Filter vehicles by type
  const getMecaVehicles = (day) => getVehiclesForDay(day).filter(v => 
    v.type_intervention === "mecanique" || v.workflow_step === "none"
  );
  
  const getCarroVehicles = (day) => getVehiclesForDay(day).filter(v => 
    v.type_intervention !== "mecanique" && v.workflow_step !== "none"
  );

  const weekNumber = getWeek(currentWeek, { weekStartsOn: 1 });

  const handlePrint = () => {
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const vehLine = (v) => `
      <div class="veh">
        <div>${v.heure_rdv ? `<span class="hr">${esc(v.heure_rdv)}</span> ` : ""}<b>${esc(v.client?.nom || "")}</b></div>
        <div>${esc(v.marque)} ${esc(v.modele)} <span class="immat">${esc(v.immatriculation)}</span></div>
        ${v.description ? `<div class="desc">${esc(v.description)}</div>` : ""}
      </div>`;
    const weeks = [];
    for (let i = 0; i < weekDays.length; i += 5) weeks.push(weekDays.slice(i, i + 5));
    const showMeca = viewMode !== "carrosserie";
    const showCarro = viewMode !== "mecanique";
    let body = "";
    weeks.forEach((days) => {
      const wn = getWeek(days[0], { weekStartsOn: 1 });
      const ann = weekAnnotations[`${days[0].getFullYear()}-${wn}`] || "";
      body += `<div class="week">
        <div class="week-title">Semaine ${wn} — du ${format(days[0], "d MMMM", { locale: fr })} au ${format(days[4], "d MMMM yyyy", { locale: fr })}${ann ? ` <span class="ann">${esc(ann)}</span>` : ""}</div>
        <table>
          <thead><tr><th class="lbl"></th>${days.map((d) => `<th>${format(d, "EEEE d", { locale: fr })}</th>`).join("")}</tr></thead>
          <tbody>
            ${showMeca ? `<tr><td class="lbl meca">Mécanique</td>${days.map((d) => {
              const c = getChargeForDay(d);
              if (c?.meca_closed) return `<td class="closed">${esc(c.meca_closed_label || "Fermé")}</td>`;
              const lines = getMecaVehicles(d).map(vehLine).join("");
              return `<td>${lines || "<span class='empty'>—</span>"}</td>`;
            }).join("")}</tr>` : ""}
            ${showCarro ? `<tr><td class="lbl carro">Carrosserie</td>${days.map((d) => {
              const c = getChargeForDay(d);
              if (c?.carro_closed) return `<td class="closed">${esc(c.carro_closed_label || "Fermé")}</td>`;
              const lines = getCarroVehicles(d).map(vehLine).join("");
              return `<td>${lines || "<span class='empty'>—</span>"}</td>`;
            }).join("")}</tr>` : ""}
          </tbody>
        </table>
      </div>`;
    });
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Planning S${weekNumber}</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family: Arial, sans-serif; color: #1e293b; margin: 0; }
        h1 { font-size: 16px; margin: 0 0 2px; }
        .sub { font-size: 11px; color: #64748b; margin-bottom: 8px; }
        .week { page-break-after: always; }
        .week:last-child { page-break-after: auto; }
        .week-title { font-size: 13px; font-weight: bold; margin: 8px 0 4px; }
        .ann { font-weight: normal; font-style: italic; color: #b45309; background: #fef3c7; padding: 1px 6px; border-radius: 4px; font-size: 11px; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        th, td { border: 1px solid #cbd5e1; padding: 4px; vertical-align: top; font-size: 10px; text-align: left; }
        th { background: #f1f5f9; text-transform: capitalize; }
        td.lbl, th.lbl { width: 68px; font-weight: bold; text-transform: uppercase; font-size: 9px; }
        td.lbl.meca { color: #047857; background: #ecfdf5; }
        td.lbl.carro { color: #b45309; background: #fffbeb; }
        td.closed { background: #fef2f2; color: #dc2626; font-weight: bold; text-align: center; }
        .veh { border-bottom: 1px dashed #e2e8f0; padding: 3px 0; }
        .veh:last-child { border-bottom: none; }
        .hr { color: #2563eb; font-weight: bold; }
        .immat { font-family: monospace; color: #475569; }
        .desc { color: #64748b; font-style: italic; }
        .empty { color: #cbd5e1; }
      </style></head><body>
      <h1>Planning — Carrosserie Auto Gardoise</h1>
      <div class="sub">Imprimé le ${format(new Date(), "d MMMM yyyy 'à' HH:mm", { locale: fr })}</div>
      ${body}
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Autorisez les pop-ups pour imprimer le planning");
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="p-2 md:p-4 min-h-[calc(100vh-64px)] md:h-[calc(100vh-64px)] flex flex-col animate-fade-in" data-testid="admin-dashboard">
      {/* Header - Responsive */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg md:text-xl font-black text-slate-900">Planning</h1>
          
          {/* Week navigation */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-bold text-slate-700 min-w-[60px] md:min-w-[80px] text-center">
              S{weekNumber}{weeksToShow > 1 ? `-S${weekNumber + weeksToShow - 1}` : ''}
            </span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Annotation semaine */}
          {(() => {
            const annKey = `${new Date(currentWeek).getFullYear()}-${weekNumber}`;
            const annText = weekAnnotations[annKey] || "";
            if (editingAnnotation === weekNumber) {
              return (
                <div className="flex items-center gap-1">
                  <input
                    className="text-xs border rounded px-2 py-1 w-48 bg-amber-50 border-amber-300 focus:outline-none focus:ring-1 focus:ring-amber-400"
                    value={annotationText}
                    onChange={(e) => setAnnotationText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveAnnotation(weekNumber); if (e.key === "Escape") setEditingAnnotation(null); }}
                    placeholder="Ex: Apprenti présent..."
                    autoFocus
                    data-testid="week-annotation-input"
                  />
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => saveAnnotation(weekNumber)}>
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                  </Button>
                </div>
              );
            }
            return (
              <div
                className={`flex items-center gap-1 cursor-pointer group ${annText ? "bg-amber-50 border border-amber-200 rounded px-2 py-0.5" : ""}`}
                onClick={() => { setEditingAnnotation(weekNumber); setAnnotationText(annText); }}
                title="Cliquez pour annoter la semaine"
                data-testid="week-annotation"
              >
                {annText ? (
                  <span className="text-xs font-medium text-amber-700">{annText}</span>
                ) : (
                  <Edit2 className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-500 transition-colors" />
                )}
              </div>
            );
          })()}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* View mode selector */}
          <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList className="bg-slate-100 h-8">
              <TabsTrigger value="split" className="text-xs px-2 md:px-3">Séparé</TabsTrigger>
              <TabsTrigger value="mecanique" className="text-xs px-2 md:px-3">Méca</TabsTrigger>
              <TabsTrigger value="carrosserie" className="text-xs px-2 md:px-3">Carro</TabsTrigger>
              <TabsTrigger value="all" className="text-xs px-2 md:px-3">Tout</TabsTrigger>
            </TabsList>
          </Tabs>
          
          {/* Weeks selector - Hidden on very small screens */}
          <div className="hidden sm:flex items-center bg-slate-100 rounded-lg p-0.5">
            {[1, 2, 3, 4].map(n => (
              <Button 
                key={n}
                variant={weeksToShow === n ? "default" : "ghost"} 
                size="sm" 
                className={`h-7 px-2 text-xs ${weeksToShow === n ? 'bg-white shadow-sm' : ''}`}
                onClick={() => setWeeksToShow(n)}
              >
                {n}S
              </Button>
            ))}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={handlePrint}
            title="Imprimer le planning affiché"
            data-testid="print-planning-btn"
          >
            <Printer className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Imprimer</span>
          </Button>
          
          <Button onClick={() => navigate("/vehicles/new")} className="bg-red-600 hover:bg-red-700 text-white h-8" size="sm">
            <Plus className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">Nouveau</span> RDV
          </Button>
        </div>
      </div>

      {/* Stats bar - Compact */}
      {stats && (
        <div className="flex items-center gap-3 mb-3 p-2 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-2">
            <Car className="h-4 w-4 text-slate-600" />
            <span className="text-sm"><strong>{stats.total}</strong> total</span>
          </div>
          <div className="h-4 w-px bg-slate-300" />
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-600" />
            <span className="text-sm"><strong>{stats.en_attente}</strong> attente</span>
          </div>
          <div className="h-4 w-px bg-slate-300" />
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-blue-600" />
            <span className="text-sm"><strong>{stats.en_cours}</strong> en cours</span>
          </div>
          <div className="h-4 w-px bg-slate-300" />
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            <span className="text-sm"><strong>{stats.termines}</strong> terminés</span>
          </div>
          {stats.blocked > 0 && (
            <>
              <div className="h-4 w-px bg-slate-300" />
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-bold">{stats.blocked} bloqués</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Calendar Grid - Stacked or Single View */}
      <div className="flex-1 overflow-x-auto overflow-y-auto">
        {viewMode === "split" ? (
          /* Stacked view: Mécanique on top, Carrosserie below */
          <div className="flex flex-col gap-2 min-h-full min-w-[640px] md:min-w-0">
            {/* MECANIQUE Section - Top with HOURS BARS */}
            <div className="bg-emerald-50/30 rounded-lg p-1.5">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <h2 className="text-[10px] font-bold text-emerald-700 uppercase">Mécanique (8h/jour)</h2>
              </div>
              <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${weekDays.length}, minmax(120px, 1fr))` }}>
                {weekDays.map((day, idx) => (
                  <MecaniqueDayColumn 
                    key={day.toISOString()} 
                    day={day} 
                    vehiclesList={getMecaVehicles(day)} 
                    loading={loading}
                    navigate={navigate}
                    workflowSteps={workflowSteps}
                    chargeData={getChargeForDay(day)}
                  />
                ))}
              </div>
            </div>
            
            {/* CARROSSERIE Section - Bottom */}
            <div className="bg-amber-50/30 rounded-lg p-1.5">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <h2 className="text-[10px] font-bold text-amber-700 uppercase">Carrosserie</h2>
              </div>
              <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${weekDays.length}, minmax(120px, 1fr))` }}>
                {weekDays.map((day, idx) => (
                  <DayColumn 
                    key={day.toISOString()} 
                    day={day} 
                    vehiclesList={getCarroVehicles(day)} 
                    colorClass="bg-amber-50"
                    loading={loading}
                    navigate={navigate}
                    getChargeForDay={getChargeForDay}
                    getStepColor={getStepColor}
                    getStepLabel={getStepLabel}
                    chargeData={getChargeForDay(day)}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Single view: Mécanique, Carrosserie, or All */
          <div className="min-h-full min-w-[640px] md:min-w-0">
            <div className="grid gap-3 h-full" style={{ gridTemplateColumns: `repeat(${weekDays.length}, minmax(120px, 1fr))` }}>
              {weekDays.map((day, idx) => {
                const vehiclesList = viewMode === "mecanique" 
                  ? getMecaVehicles(day)
                  : viewMode === "carrosserie"
                    ? getCarroVehicles(day)
                    : getVehiclesForDay(day);
                
                return (
                  <DayColumn 
                    key={day.toISOString()} 
                    day={day} 
                    vehiclesList={vehiclesList}
                    colorClass={viewMode === "mecanique" ? "bg-blue-50" : viewMode === "carrosserie" ? "bg-amber-50" : "bg-slate-50"}
                    loading={loading}
                    navigate={navigate}
                    getChargeForDay={getChargeForDay}
                    getStepColor={getStepColor}
                    getStepLabel={getStepLabel}
                    chargeData={getChargeForDay(day)}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
